import { Modal, ModalContent } from "@chakra-ui/modal";
import {
  Sequence,
  Technique,
  TechniqueContext,
} from "domain/entities/techniques.entities";
import {
  createTechnique,
  getTechnique,
  updateSequences,
} from "domain/techniques/techniques.actions";
import { selectIsTechniqueLoading } from "domain/techniques/techniques.selectors";
import { useEffect, useState } from "react";
import { HiArrowLeft } from "react-icons/hi";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { TechniqueBanner } from "ui/pages/app/techniques/technique/TechniqueBanner";
import { EditSequenceComponent } from "ui/pages/app/seances/create-seance/steps/add-content/modals/EditSequence";
import {
  formatDuration,
  getTotalSequencesDuration,
  parseDuration,
} from "ui/utils/formatters";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { translate } from "ui/i18n";
import { selectUserId } from "domain/auth/auth.selectors";
import { Consignes } from "ui/pages/app/components/activities/techniques/Consignes";
import { useForm } from "react-hook-form";
import { Consigne } from "domain/entities/consignes.entities";
import {
  createConsigne,
  deleteConsigne,
  updateConsigne,
} from "domain/consignes/consignes.actions";
import { Snackbar } from "ui/pages/snackbar";

interface IProps {
  isOpen: boolean;
  partialTechnique: Technique;
  closeEditTechniqueModal: (openView: boolean) => void;
  addTechnique?: (
    techniqueId: number,
    context: TechniqueContext,
    techniqueToRemoveOrder: number,
    techniqueToRemoveId: number
  ) => Promise<{
    success: boolean;
  }>;
  updateSelectedTechniques?: (
    technique: Technique,
    action: "add" | "remove"
  ) => void;
  fetchSeance?: () => Promise<void>;
  seanceTechniqueOrder?: number;
  seanceTechniqueId?: number;
}

export function ModalEditTechnique({
  isOpen,
  partialTechnique,
  closeEditTechniqueModal,
  addTechnique,
  updateSelectedTechniques,
  fetchSeance,
  seanceTechniqueOrder,
  seanceTechniqueId,
}: IProps) {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectIsTechniqueLoading);
  const userId = useAppSelector(selectUserId);
  const [technique, setTechnique] = useState<Technique | null>(null);
  const [sequences, setSequences] = useState<
    (Sequence & { deleted: boolean })[]
  >([]);
  const [removedConsignes, setRemovedConsignes] = useState<number[]>([]);
  const consigneForm = useForm<{ consignes: Consigne[] }>({
    defaultValues: {
      consignes: [],
    },
  });

  useEffect(() => {
    if (isOpen) {
      fetchTechnique();
    } else {
      consigneForm.reset();
    }
  }, [isOpen]); //eslint-disable-line

  useEffect(() => {
    setSequences(
      technique?.sequences.map((sequence) => ({
        ...sequence,
        deleted: false,
        duration: formatDuration(sequence.duration),
      })) || []
    );
    consigneForm.reset({
      consignes:
        technique?.consignes.map((consigne) => ({
          ...consigne,
          duration: formatDuration(consigne.duration),
        })) || [],
    });
    setRemovedConsignes([]);
  }, [technique]); //eslint-disable-line

  const handleCloseModal = (openView: boolean) => {
    setSequences([]);
    closeEditTechniqueModal(openView);
  };

  const fetchTechnique = async () => {
    if (partialTechnique?.id) {
      const result = await dispatch(
        getTechnique({ id: Number(partialTechnique.id), consignes: true })
      ).unwrap();
      if (result.success) {
        setTechnique(result.technique);
      } else setTechnique(null);
    }
  };

  const manageConsignes = async (
    consignes: Consigne[],
    techniqueId: number,
    isCreatingTechnique: boolean
  ) => {
    if (removedConsignes.length > 0) {
      for (const consigneId of removedConsignes) {
        await dispatch(deleteConsigne(consigneId));
      }
    }
    consignes.forEach(async (consigne, index) => {
      !isCreatingTechnique && consigne && consigne.id
        ? await dispatch(
            updateConsigne({
              title: consigne.title,
              content: consigne.content,
              duration: parseDuration(consigne.duration),
              id: consigne.id,
              order: index,
            })
          ).unwrap()
        : await dispatch(
            createConsigne({
              title: consigne.title,
              content: consigne.content,
              duration: parseDuration(consigne.duration),
              techniqueId,
              order: index,
              userId,
            })
          ).unwrap();
    });
  };

  const saveTechnique = async (consignesData: { consignes: Consigne[] }) => {
    if (!technique) return;

    const filteredSequences = sequences.filter((sequence) => !sequence.deleted);

    const requestBody = {
      technique: {
        title: technique.title,
        duration: getTotalSequencesDuration(filteredSequences).toString(),
        type: technique.type,
        participants: technique.participants,
        level: technique.level,
        context: technique.context,
        remote: technique.remote,
        onsite: technique.onsite,
        participantsMin: technique.participantsMin,
        participantsMax: technique.participantsMax,
        objectif: technique.objectif,
        id: technique.id,
      },
      sequences: filteredSequences.map((sequence) => ({
        duration: parseDuration(sequence.duration),
        storyline: sequence.storyline.replaceAll(
          /\n\n(?=[a-zA-Z])/g,
          "  \n\\\n"
        ),
        title: sequence.title || "",
        id: sequence.id,
      })),
    };

    if (technique.userId === userId) {
      const result = await dispatch(
        updateSequences({
          deletedSequences: sequences
            .filter((sequence) => sequence.deleted)
            .map((s) => s.id),
          sequences: requestBody.sequences,
        })
      ).unwrap();
      if (result.success) {
        await manageConsignes(consignesData.consignes, technique.id, false);
        await fetchSeance();
        Snackbar.showSuccess({
          title: translate("ns1:CreateSeance.EditedTechnique"),
          variant: "secondary",
        });
        handleCloseModal(false);
      }
    } else {
      const result = await dispatch(createTechnique(requestBody)).unwrap();
      if (result.success) {
        await manageConsignes(
          consignesData.consignes,
          result.technique.id,
          true
        );
        if (
          addTechnique &&
          seanceTechniqueOrder !== undefined &&
          seanceTechniqueId !== undefined
        ) {
          addTechnique(
            result.technique.id,
            technique.context as TechniqueContext,
            seanceTechniqueOrder,
            seanceTechniqueId
          );
        } else if (updateSelectedTechniques) {
          updateSelectedTechniques(result.technique, "add");
        }
        Snackbar.showSuccess({
          title: translate("ns1:CreateSeance.EditedTechnique"),
          variant: "secondary",
        });
        handleCloseModal(false);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => handleCloseModal(true)} size="full">
      <ModalContent>
        <div className="flex min-h-dvh flex-col bg-darkGrey-100 p-4 md:flex-row md:py-8 md:pl-6 md:pr-10">
          <div className="mb-4 flex w-full items-center justify-between md:my-0 md:w-12 md:items-start">
            <button
              onClick={() => handleCloseModal(true)}
              className="z-50 flex h-5 items-center"
            >
              <HiArrowLeft size={24} className="text-darkGrey-800" />
            </button>
          </div>

          {technique && !isLoading ? (
            <div className="w-full">
              <div className="flex w-full flex-col space-y-6 md:space-y-8">
                <TechniqueBanner technique={technique} checkIcon />

                <div className="flex flex-col space-x-0 space-y-6 md:flex-row md:space-x-8 md:space-y-0">
                  <div className="flex w-full flex-col space-y-6 md:space-y-8">
                    <div className="rounded-[30px] bg-white p-4 py-6 md:p-6">
                      {sequences.length > 0 &&
                        sequences
                          .filter((seq) => !seq.deleted)
                          .map((sequence, index) => (
                            <EditSequenceComponent
                              key={"sequence" + index}
                              sequence={sequence}
                              index={index}
                              sequencesLength={sequences?.length}
                              sequences={sequences}
                              setSequences={setSequences}
                            />
                          ))}
                    </div>

                    <div className="mt-6 rounded-[30px] bg-white p-4 py-6 md:mt-8 md:p-6">
                      <Consignes
                        consigneForm={consigneForm}
                        setRemovedConsignes={setRemovedConsignes}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-dvh w-full items-center justify-center">
              <div className="loader" />
            </div>
          )}
        </div>
        <div className="sticky bottom-0 z-[100] flex w-full flex-col items-center bg-white px-6 py-4 shadow-lg">
          <div className="w-full max-w-[590px]">
            <PrimaryButton
              type="submit"
              height="46px"
              width="100%"
              onClick={consigneForm.handleSubmit(saveTechnique)}
            >
              {translate("ns1:CreateSeance.SaveAndAddToSeance")}
            </PrimaryButton>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
